/**
 * useM3Motion - Accessibility-aware M3 Motion Hook
 * 
 * Wraps M3 Expressive motion tokens with automatic accessibility checks.
 * When prefers-reduced-motion is active, provides instant crossfade (50ms)
 * instead of full spring animations.
 * 
 * @see https://m3.material.io/styles/motion/easing-and-duration/tokens-specs
 */

import { useMemo, useCallback } from 'react';
import { useAccessibility } from '../services/accessibility';
import {
  emphasizedEasing,
  standardEasing,
  springEasing,
  shortDuration,
  mediumDuration,
  longDuration,
  transitions,
  m3Sx,
  keyframes,
  staggerDelay,
  multiPropertyTransition,
  getDurationForDistance,
} from '../theme/m3Motion';

// Reduced motion uses instant crossfade per user requirement (50ms)
const REDUCED_MOTION_DURATION = 50;
const REDUCED_MOTION_EASING = 'linear';

// Default stagger interval for FAB cascades (per user requirement: 50ms)
const DEFAULT_STAGGER_MS = 50;

export interface M3MotionConfig {
  /** Override the default stagger interval (ms) */
  staggerInterval?: number;
  /** Force enable/disable motion (overrides system preference) */
  forceMotion?: boolean;
}

export interface M3MotionReturn {
  /** Whether reduced motion is preferred */
  prefersReducedMotion: boolean;
  
  /** Get duration based on accessibility - returns 50ms if reduced motion */
  getDuration: (normalDuration: number) => number;
  
  /** Get easing based on accessibility - returns linear if reduced motion */
  getEasing: (normalEasing: string) => string;
  
  /** Get full transition string, accessibility-aware */
  getTransition: (
    property: string | string[],
    duration: number,
    easing?: string
  ) => string;
  
  /** Get stagger delay for cascading animations (e.g., FAB mini buttons) */
  getStaggerDelay: (index: number, baseDelay?: number) => number;
  
  /** Get stagger delay as CSS string */
  getStaggerDelayCSS: (index: number, baseDelay?: number) => string;
  
  /** Pre-built accessible transitions */
  accessibleTransitions: {
    fab: string;
    fabMini: string;
    chip: string;
    dialog: string;
    button: string;
    color: string;
    all: string;
  };
  
  /** Pre-built accessible MUI sx props */
  accessibleSx: {
    fab: Record<string, unknown>;
    fabMini: Record<string, unknown>;
    chip: Record<string, unknown>;
    button: Record<string, unknown>;
    card: Record<string, unknown>;
  };
  
  /** Raw motion tokens for custom use (always full values, not reduced) */
  tokens: {
    easing: {
      emphasized: string;
      standard: string;
      spring: string;
    };
    duration: {
      short1: number;
      short2: number;
      short3: number;
      short4: number;
      medium1: number;
      medium2: number;
      medium3: number;
      medium4: number;
      long1: number;
      long2: number;
      long3: number;
      long4: number;
    };
    keyframes: typeof keyframes;
  };
  
  /** Utility functions */
  utils: {
    staggerDelay: typeof staggerDelay;
    multiPropertyTransition: typeof multiPropertyTransition;
    getDurationForDistance: typeof getDurationForDistance;
  };
}

/**
 * Hook for accessibility-aware M3 Expressive motion
 * 
 * @example
 * ```tsx
 * const { getTransition, accessibleSx, prefersReducedMotion } = useM3Motion();
 * 
 * // Use pre-built accessible sx props
 * <Fab sx={accessibleSx.fab}>...</Fab>
 * 
 * // Or build custom transitions
 * <Box sx={{ transition: getTransition('transform', 300, emphasizedEasing) }}>
 * ```
 */
export function useM3Motion(config: M3MotionConfig = {}): M3MotionReturn {
  const { staggerInterval = DEFAULT_STAGGER_MS, forceMotion } = config;
  const { prefersReducedMotion: systemPrefersReducedMotion } = useAccessibility();
  
  // Allow force override for testing or specific UI needs
  const prefersReducedMotion = forceMotion !== undefined 
    ? !forceMotion 
    : systemPrefersReducedMotion;

  // Memoize duration getter
  const getDuration = useCallback(
    (normalDuration: number): number => {
      return prefersReducedMotion ? REDUCED_MOTION_DURATION : normalDuration;
    },
    [prefersReducedMotion]
  );

  // Memoize easing getter
  const getEasing = useCallback(
    (normalEasing: string): string => {
      return prefersReducedMotion ? REDUCED_MOTION_EASING : normalEasing;
    },
    [prefersReducedMotion]
  );

  // Build transition string with accessibility
  const getTransition = useCallback(
    (
      property: string | string[],
      duration: number,
      easing: string = emphasizedEasing.default
    ): string => {
      const actualDuration = getDuration(duration);
      const actualEasing = getEasing(easing);
      
      if (Array.isArray(property)) {
        return property
          .map((p) => `${p} ${actualDuration}ms ${actualEasing}`)
          .join(', ');
      }
      
      return `${property} ${actualDuration}ms ${actualEasing}`;
    },
    [getDuration, getEasing]
  );

  // Stagger delay for cascading animations
  const getStaggerDelay = useCallback(
    (index: number, baseDelay: number = 0): number => {
      if (prefersReducedMotion) {
        // No stagger for reduced motion - all items appear together
        return baseDelay;
      }
      return baseDelay + index * staggerInterval;
    },
    [prefersReducedMotion, staggerInterval]
  );

  // CSS-ready stagger delay
  const getStaggerDelayCSS = useCallback(
    (index: number, baseDelay: number = 0): string => {
      return `${getStaggerDelay(index, baseDelay)}ms`;
    },
    [getStaggerDelay]
  );

  // Pre-built accessible transitions
  const accessibleTransitions = useMemo(
    () => ({
      fab: prefersReducedMotion
        ? `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.fabState,
      fabMini: prefersReducedMotion
        ? `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.miniFabPopout,
      chip: prefersReducedMotion
        ? `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.chipSelect,
      dialog: prefersReducedMotion
        ? `opacity ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.modalEnter,
      button: prefersReducedMotion
        ? `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.buttonHover,
      color: prefersReducedMotion
        ? `background-color ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}, color ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : transitions.colorChange,
      all: prefersReducedMotion
        ? `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`
        : `all ${mediumDuration.medium2}ms ${emphasizedEasing.default}`,
    }),
    [prefersReducedMotion]
  );

  // Pre-built accessible MUI sx props
  const accessibleSx = useMemo(
    () => ({
      fab: prefersReducedMotion
        ? {
            transition: `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`,
            '&:hover': { transform: 'none' },
            '&:active': { transform: 'scale(0.98)' },
          }
        : m3Sx.fabExpressive,
      fabMini: prefersReducedMotion
        ? {
            transition: `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`,
            '&:hover': { transform: 'none' },
            '&:active': { transform: 'scale(0.95)' },
          }
        : m3Sx.fabExpressive,
      chip: prefersReducedMotion
        ? {
            transition: `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`,
            '&:hover': { transform: 'none' },
            '&:active': { transform: 'scale(0.98)' },
          }
        : m3Sx.chipInteractive,
      button: prefersReducedMotion
        ? {
            transition: `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`,
            '&:hover': { transform: 'none' },
            '&:active': { transform: 'scale(0.98)' },
          }
        : m3Sx.buttonHover,
      card: prefersReducedMotion
        ? {
            transition: `all ${REDUCED_MOTION_DURATION}ms ${REDUCED_MOTION_EASING}`,
            '&:hover': { transform: 'none' },
          }
        : m3Sx.cardLift,
    }),
    [prefersReducedMotion]
  );

  // Raw tokens for advanced use
  const tokens = useMemo(
    () => ({
      easing: {
        emphasized: emphasizedEasing.default,
        standard: standardEasing.default,
        spring: springEasing.gentle,
      },
      duration: {
        short1: shortDuration.short1,
        short2: shortDuration.short2,
        short3: shortDuration.short3,
        short4: shortDuration.short4,
        medium1: mediumDuration.medium1,
        medium2: mediumDuration.medium2,
        medium3: mediumDuration.medium3,
        medium4: mediumDuration.medium4,
        long1: longDuration.long1,
        long2: longDuration.long2,
        long3: longDuration.long3,
        long4: longDuration.long4,
      },
      keyframes,
    }),
    []
  );

  // Utility functions pass-through
  const utils = useMemo(
    () => ({
      staggerDelay,
      multiPropertyTransition,
      getDurationForDistance,
    }),
    []
  );

  return {
    prefersReducedMotion,
    getDuration,
    getEasing,
    getTransition,
    getStaggerDelay,
    getStaggerDelayCSS,
    accessibleTransitions,
    accessibleSx,
    tokens,
    utils,
  };
}

export default useM3Motion;
